/**
 * SubscriptionTab.jsx — Onglet "Abonnement" : plan actuel + comparatif vertical des 5
 * formules Body-Up (cartes cliquables, sans scroll horizontal, 100% mobile-first).
 * Chaque carte affiche ses fonctionnalités en delta ("Tout du plan précédent, plus...")
 * pour comparer implicitement les paliers sans tableau.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, ChevronDown, CreditCard, Sparkles, Crown, ShieldCheck, Loader2,
  Users, Dumbbell, BadgeCheck, Calendar, Receipt, Download, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../context/ClientAuthContext';
import { ReferralCard } from './ReferralCard';
import { SoloAnnualPromoCard } from '../../components/shared';

const INVOICE_STATUS = {
  paid: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Payée' },
  pending: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'En attente' },
  unpaid: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'En attente' },
  scheduled: { bg: 'bg-zinc-500/15', text: 'text-zinc-400', label: 'Programmée' },
};

const BillingStatusPill = ({ status }) => {
  const cfg = INVOICE_STATUS[status] || { bg: 'bg-zinc-600/15', text: 'text-zinc-400', label: status || '—' };
  return <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
};

const ACCENTS = {
  zinc: { badge: 'bg-zinc-700/40 text-zinc-300', ring: 'border-white/10', grad: 'from-zinc-700 to-zinc-600', check: 'text-zinc-400' },
  yellow: { badge: 'bg-yellow-500/15 text-yellow-300', ring: 'border-yellow-500/30', grad: 'from-yellow-400 to-primary', check: 'text-yellow-400' },
  blue: { badge: 'bg-sky-500/15 text-sky-300', ring: 'border-sky-500/30', grad: 'from-sky-500 to-primary', check: 'text-sky-400' },
  pink: { badge: 'bg-primary/15 text-primary', ring: 'border-primary/30', grad: 'from-primary to-pink-500', check: 'text-primary' },
  purple: { badge: 'bg-purple-500/15 text-purple-300', ring: 'border-purple-500/30', grad: 'from-purple-500 to-primary', check: 'text-purple-400' },
};

const buildPlans = (L, pricing, soloTrialUsed) => {
  const monthlyPrice = pricing ? pricing.monthly.price.toFixed(2) : '9.90';
  const yearlyPrice = pricing ? (pricing.yearly.price % 1 === 0 ? pricing.yearly.price.toFixed(0) : pricing.yearly.price.toFixed(2)) : '79';
  const yearlyPromo = pricing?.yearly?.promo_active;
  const soloAnnual = pricing?.solo_annual;
  const soloAnnualPromo = soloAnnual?.promo_active;
  const soloAnnualPrice = soloAnnual ? (soloAnnual.price % 1 === 0 ? soloAnnual.price.toFixed(0) : soloAnnual.price.toFixed(2)) : '199';
  return [
  {
    key: 'free', name: L('Gratuit', 'Free'), price: '0', unit: L('à vie', 'forever'), accent: 'zinc', icon: ShieldCheck,
    tagline: L('Le calculateur + le suivi de base, sans limite de temps', 'The calculator + basic tracking, no time limit'),
    features: [
      L('Calculateur de calories & macros', 'Calorie & macro calculator'),
      L('Suivi du poids & des mensurations (avatar interactif)', 'Weight & measurements tracking (interactive avatar)'),
      L('Journal alimentaire manuel', 'Manual food journal'),
      L('Historique des 7 derniers jours', 'Last 7 days history'),
    ],
    isCurrent: (sub) => !sub.plan,
  },
  {
    key: 'suivi', name: 'Suivi Pro', price: monthlyPrice, unit: L(`/ mois (essai 7j gratuit, ou ${yearlyPrice}.-/an${yearlyPromo ? ' promo' : ''})`, `/ month (7-day free trial, or ${yearlyPrice}.-/yr${yearlyPromo ? ' promo' : ''})`), accent: 'yellow', icon: Sparkles,
    tagline: L('Scan photo IA, recherche illimitée, historique complet', 'AI photo scan, unlimited search, full history'),
    features: [
      L('Tout du plan Gratuit, plus :', 'Everything in Free, plus:'),
      L('📸 Scan IA du plat → macros estimées', '📸 AI meal scan → estimated macros'),
      L('Recherche AI illimitée des aliments', 'Unlimited AI food search'),
      L('Historique de journal illimité', 'Unlimited journal history'),
      L('Photos de transformation IA', 'AI transformation photos'),
    ],
    isCurrent: (sub) => ['monthly', 'yearly'].includes(sub.plan),
    includedByOtherPlan: (sub) => !!sub.premium && !['monthly', 'yearly'].includes(sub.plan),
    checkoutOptions: [
      { id: 'monthly', label: L(`Essai 7j gratuit, puis ${monthlyPrice} CHF / mois`, `7-day free trial, then ${monthlyPrice} CHF / month`) },
      { id: 'yearly', label: L(`${yearlyPrice}.- CHF / an${yearlyPromo ? ` (-${pricing.yearly.discount_pct}% jusqu'au 31 août)` : ''}`, `${yearlyPrice}.- CHF / yr${yearlyPromo ? ` (-${pricing.yearly.discount_pct}% until Aug 31)` : ''}`) },
    ],
    checkoutKind: 'suivi',
  },
  {
    key: 'solo', name: 'Suivi Pro+', price: '19.90', unit: L(`/ mois${soloTrialUsed ? '' : ' (essai 7j gratuit)'}, ou ${soloAnnualPromo ? `${soloAnnualPrice}.-/an (-${soloAnnual.discount_pct}%)` : '199.-/an (2 mois offerts)'}`, `/ month${soloTrialUsed ? '' : ' (7-day free trial)'}, or ${soloAnnualPromo ? `CHF ${soloAnnualPrice}/yr (-${soloAnnual.discount_pct}%)` : 'CHF 199/yr (2 months free)'}`), accent: 'blue', icon: Dumbbell,
    tagline: L('Musculation en autonomie (DIY), sans coach humain', 'Self-guided training (DIY), no human coach'),
    features: [
      L('Tout du Suivi Pro, plus :', 'Everything in Suivi Pro, plus:'),
      L('Bibliothèque de 449 exercices + créateur de programme illimité', '449-exercise library + unlimited program builder'),
      L("Coach IA pour la sélection d'exercices (à l'usage)", 'AI coach for exercise selection (pay-per-use)'),
      L('Sans engagement · 100% en autonomie', 'No commitment · fully self-guided'),
    ],
    isCurrent: (sub) => sub.plan === 'solo',
    includedByOtherPlan: (sub) => ['communaute', 'transformation', 'vip'].includes(sub.plan),
    checkoutOptions: [
      {
        id: 'solo',
        label: soloTrialUsed
          ? L('19.90.- CHF / mois', '19.90.- CHF / month')
          : L('Essai 7j gratuit, puis 19.90 CHF / mois', '7-day free trial, then 19.90 CHF / month'),
      },
      {
        id: 'solo_annual',
        label: soloAnnualPromo
          ? L(`🔥 -${soloAnnual.discount_pct}% : ${soloAnnualPrice}.- CHF/an (au lieu de ${soloAnnual.original_price}.-) + essai 7j gratuit`, `🔥 -${soloAnnual.discount_pct}%: CHF ${soloAnnualPrice}/yr (was CHF ${soloAnnual.original_price}) + 7-day free trial`)
          : L('199.- CHF / an · 🎁 2 mois offerts', '199.- CHF / yr · 🎁 2 months free'),
      },
    ],
    checkoutKind: 'transformation',
  },
  {
    key: 'communaute', name: L('Communauté', 'Community'), price: '49', unit: L('/ mois', '/ month'), accent: 'pink', icon: Users,
    tagline: L('Programme perso + messagerie coach, sans engagement', 'Custom program + coach messaging, no commitment'),
    features: [
      L('Tout du Suivi Pro, plus :', 'Everything in Suivi Pro, plus:'),
      L('Programme de musculation personnalisé (par Laurent)', 'Personalized training program (by Laurent)'),
      L('Plan alimentaire perso renouvelé chaque mois', 'Personalized meal plan refreshed monthly'),
      L('Messagerie directe avec le coach', 'Direct coach messaging'),
      L('Accès à la Communauté Body-Up', 'Access to the Body-Up Community'),
    ],
    isCurrent: (sub) => sub.plan === 'communaute',
    includedByOtherPlan: (sub) => ['transformation', 'vip'].includes(sub.plan),
    checkoutOptions: [{ id: 'communaute', label: L('49.- CHF / mois', '49.- CHF / month') }],
    checkoutKind: 'transformation',
  },
  {
    key: 'transfo', name: L('100% en ligne', '100% Online'), price: '299', unit: L('/ mois · 6 mois', '/ month · 6 months'), accent: 'purple', icon: Dumbbell,
    tagline: L('Coaching complet avec Laurent, visios incluses', 'Full coaching with Laurent, calls included'),
    features: [
      L('Tout de la Communauté, plus :', 'Everything in Community, plus:'),
      L('Onboarding personnalisé avec Laurent', 'Personalized onboarding with Laurent'),
      L('6 visios Zoom 30 min incluses (≈1/mois)', '6 included 30-min Zoom calls (~1/month)'),
      L('Programme de muscu sur-mesure chaque mois', 'Custom training program every month'),
      L('Messagerie 24/7 + ajustements réguliers', '24/7 messaging + regular adjustments'),
    ],
    isCurrent: (sub) => sub.plan === 'transformation',
    checkoutOptions: [
      { id: 'monthly', label: L('299.- CHF / mois', '299.- CHF / month') },
      { id: 'upfront6', label: L('1704.30 CHF en 1x (-5%, 6 mois)', 'CHF 1704.30 upfront (-5%, 6 months)') },
    ],
    checkoutKind: 'transformation',
  },
  {
    key: 'vip', name: L('Hybride VIP', 'VIP Hybrid'), price: '399', unit: L('/ mois · 6 mois', '/ month · 6 months'), accent: 'purple', icon: Crown,
    tagline: L('Le présentiel à Nyon + tout le 100% en ligne', 'In-person in Nyon + everything online'),
    features: [
      L('Tout du 100% en ligne (hors visios Zoom), plus :', 'Everything in 100% Online (except Zoom calls), plus:'),
      L("6 séances 60' au choix : studio Nyon ou Zoom", "6x 60' sessions, your choice: Nyon studio or Zoom"),
      L('2 scans 3D complets (début + fin)', '2 full 3D scans (start + end)'),
      L('Expérience premium & accès salle Body-Up', 'Premium experience & Body-Up studio access'),
    ],
    isCurrent: (sub) => sub.plan === 'vip',
    checkoutOptions: [
      { id: 'vip_monthly', label: L('399.- CHF / mois', '399.- CHF / month') },
      { id: 'vip_upfront6', label: L('2274.30 CHF en 1x (-5%, 6 mois)', 'CHF 2274.30 upfront (-5%, 6 months)') },
    ],
    checkoutKind: 'transformation',
  },
];
};

// Formules à option de paiement unique -> éligibles à l'ouverture directe depuis un lien
// externe (?plan=X&autopay=1) sans ambiguïté sur quel tarif choisir.
const AUTO_CHECKOUT_MAP = {
  solo: { checkoutKind: 'transformation', optionId: 'solo' },
  communaute: { checkoutKind: 'transformation', optionId: 'communaute' },
};

export const SubscriptionTab = ({ en }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const location = useLocation();
  const [sub, setSub] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [billing, setBilling] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [managingSub, setManagingSub] = useState(false);
  const plans = buildPlans(L, pricing, sub?.solo_trial_used);

  useEffect(() => {
    authFetch('/api/tracking/subscription/pricing').then((r) => r.json()).then(setPricing).catch(() => {});
    authFetch('/api/tracking/subscription').then((r) => r.json()).then(setSub).catch(() => setSub({}));
    authFetch('/api/tracking/my-invoices').then((r) => r.json()).then(setBilling).catch(() => setBilling({ invoices: [], sessions: [] }));
  }, [authFetch]);

  // Point d'entrée unique pour tous les liens "rejoindre ce pack" du site : le marketing
  // pointe vers /mon-espace?tab=profil&plan=X(&autopay=1) plutôt que vers des tunnels de
  // paiement séparés — une seule implémentation de checkout à maintenir, toujours avec le
  // bon email (celui de la session connectée).
  useEffect(() => {
    if (!sub) return;
    const params = new URLSearchParams(location.search);
    const planKey = params.get('plan');
    if (!planKey || !plans.some((p) => p.key === planKey)) return;
    setExpanded(planKey);
    document.getElementById('abonnement-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const target = plans.find((p) => p.key === planKey);
    const alreadyHas = target.isCurrent(sub) || target.includedByOtherPlan?.(sub);
    if (alreadyHas || params.get('autopay') !== '1') return;
    const auto = AUTO_CHECKOUT_MAP[planKey];
    if (auto) startCheckout(target, auto.optionId);
  }, [sub]);

  const downloadInvoicePdf = async (invoiceNumber) => {
    setDownloadingId(invoiceNumber);
    try {
      const res = await authFetch(`/api/tracking/my-invoices/${encodeURIComponent(invoiceNumber)}/pdf`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_Body-Up_${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(L('Téléchargement impossible.', 'Download failed.'));
    } finally {
      setDownloadingId(null);
    }
  };

  const startCheckout = async (plan, optionId) => {
    setCheckingOut(`${plan.key}:${optionId}`);
    try {
      const origin_url = window.location.origin;
      const url = plan.checkoutKind === 'suivi' ? '/api/tracking/subscription/checkout' : '/api/transformation/checkout';
      const body = plan.checkoutKind === 'suivi' ? { plan: optionId, origin_url } : { option: optionId, origin_url };
      const res = await authFetch(url, { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error();
    } catch {
      toast.error(L('Erreur lors de la création du paiement.', 'Error creating the payment session.'));
      setCheckingOut(null);
    }
  };

  const manageSubscription = async () => {
    setManagingSub(true);
    try {
      const res = await authFetch('/api/tracking/subscription/portal-link');
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error();
    } catch {
      toast.error(L('Impossible d\'ouvrir le portail de gestion pour le moment.', 'Could not open the management portal right now.'));
      setManagingSub(false);
    }
  };

  if (!sub) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const currentPlan = plans.find((p) => p.isCurrent(sub)) || plans[0];
  const renewalDate = sub.current_period_end
    ? new Date(sub.current_period_end).toLocaleDateString(en ? 'en-US' : 'fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="space-y-5 pb-24" data-testid="subscription-tab">
      <h2 className="text-xl font-black text-white flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-primary" /> {L('Mon abonnement', 'My subscription')}
      </h2>

      {/* Plan actuel */}
      <div className={`rounded-2xl border ${ACCENTS[currentPlan.accent].ring} bg-gradient-to-br from-zinc-900 to-black p-5`} data-testid="current-plan-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${ACCENTS[currentPlan.accent].grad} flex items-center justify-center shrink-0`}>
              <currentPlan.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-bold tracking-wider text-zinc-500 uppercase">{L('Formule actuelle', 'Current plan')}</p>
              <p className="text-lg font-black text-white" data-testid="current-plan-name">{currentPlan.name}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
            <BadgeCheck className="w-3.5 h-3.5" /> {sub.status === 'trialing' ? L('Essai gratuit', 'Free trial') : L('Actif', 'Active')}
          </span>
        </div>
        {renewalDate && (
          <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {sub.status === 'trialing' ? L('Premier débit le', 'First charge on') : L('Prochain renouvellement', 'Next renewal')} : {renewalDate}
          </p>
        )}
        {currentPlan.key === 'free' && (
          <p className="text-xs text-zinc-500 mt-3">{L('Débloquez plus de fonctionnalités ci-dessous à tout moment.', 'Unlock more features below anytime.')}</p>
        )}
        {sub.is_recurring && (
          <button
            onClick={manageSubscription}
            disabled={managingSub}
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-60"
            data-testid="manage-subscription-btn"
          >
            {managingSub ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {L('Gérer mon abonnement (annuler, changer de carte)', 'Manage my subscription (cancel, update card)')}
          </button>
        )}
      </div>

      <ReferralCard en={en} />

      {pricing?.solo_annual?.promo_active && (
        <SoloAnnualPromoCard
          promo={{
            active: true,
            price: pricing.solo_annual.price,
            originalPrice: pricing.solo_annual.original_price,
            discountPct: pricing.solo_annual.discount_pct,
            endsAt: pricing.solo_annual.promo_ends_at,
          }}
          language={en ? 'en' : 'fr'}
          ctaHref="/mon-espace?tab=profil&plan=solo"
        />
      )}

      {/* Comparatif vertical par cartes cliquables */}
      <div className="space-y-3">
        {plans.map((p) => {
          const isOpen = expanded === p.key;
          const isCurrent = p.key === currentPlan.key;
          const isIncluded = !isCurrent && p.includedByOtherPlan?.(sub);
          const a = ACCENTS[p.accent];
          return (
            <div key={p.key} className={`rounded-2xl border ${isCurrent ? a.ring : 'border-white/10'} bg-zinc-900/60 overflow-hidden`} data-testid={`plan-card-${p.key}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : p.key)}
                className="w-full flex items-center justify-between gap-3 p-4 text-left"
                data-testid={`plan-toggle-${p.key}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${a.grad} flex items-center justify-center shrink-0`}>
                    <p.icon className="w-4.5 h-4.5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-white text-sm">{p.name}</p>
                      {isCurrent && <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${a.badge}`}>{L('ACTUEL', 'CURRENT')}</span>}
                      {isIncluded && <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/15 text-emerald-400" data-testid={`plan-included-badge-${p.key}`}>{L('INCLUS', 'INCLUDED')}</span>}
                    </div>
                    <p className="text-xs text-zinc-500 truncate">{p.tagline}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-right">
                    <p className="font-black text-white text-base leading-tight">{p.price}<span className="text-[10px] text-zinc-500"> CHF</span></p>
                    <p className="text-[9px] text-zinc-500 whitespace-nowrap">{p.unit}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                    <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
                      <ul className="space-y-2">
                        {p.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                            {f.endsWith(':') ? <span className="text-zinc-500 text-xs font-bold italic">{f}</span> : (<><Check className={`w-4 h-4 mt-0.5 shrink-0 ${a.check}`} /> {f}</>)}
                          </li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <div className="text-center py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-sm font-bold" data-testid={`plan-active-badge-${p.key}`}>
                          <BadgeCheck className="w-4 h-4 inline mr-1.5 -mt-0.5" /> {L('Votre formule actuelle', 'Your current plan')}
                        </div>
                      ) : isIncluded ? (
                        <div className="text-center py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-sm font-bold" data-testid={`plan-included-notice-${p.key}`}>
                          <BadgeCheck className="w-4 h-4 inline mr-1.5 -mt-0.5" /> {L(`Déjà inclus dans votre formule ${currentPlan.name}, aucun paiement supplémentaire`, `Already included in your ${currentPlan.name} plan, no extra payment`)}
                        </div>
                      ) : p.checkoutOptions ? (
                        <div className="flex flex-col gap-2">
                          {p.checkoutOptions.map((opt) => (
                            <div key={opt.id} className="relative">
                              {opt.id.endsWith('_annual') && (
                                <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold text-black bg-yellow-400 shadow" data-testid={`plan-best-value-badge-${p.key}-${opt.id}`}>
                                  ⭐ {L('Meilleure offre', 'Best value')}
                                </span>
                              )}
                              <button
                                onClick={() => startCheckout(p, opt.id)}
                                disabled={checkingOut === `${p.key}:${opt.id}`}
                                className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r ${a.grad} hover:scale-[1.02] transition-transform disabled:opacity-60`}
                                data-testid={`plan-checkout-${p.key}-${opt.id}`}
                              >
                                {checkingOut === `${p.key}:${opt.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {opt.label}
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-2.5 rounded-xl bg-zinc-800/60 border border-white/10 text-zinc-400 text-sm">
                          {L('Inclus automatiquement, aucune action requise.', 'Included automatically, no action needed.')}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Mes factures — historique de facturation (virement QR-code + Stripe) */}
      <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-5" data-testid="my-invoices-section">
        <h3 className="text-base font-black text-white flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-primary" /> {L('Mes factures', 'My invoices')}
        </h3>
        {!billing ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : (billing.invoices?.length === 0 && billing.sessions?.length === 0) ? (
          <p className="text-zinc-500 text-sm text-center py-4">{L('Aucune facture pour le moment.', 'No invoices yet.')}</p>
        ) : (
          <div className="space-y-3">
            {(billing.invoices || []).map((i) => (
              <div key={i.invoice_number} className="flex items-center justify-between gap-3 rounded-xl bg-zinc-800/50 border border-white/5 p-3" data-testid={`my-invoice-row-${i.invoice_number}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{i.description || 'Facture Body-Up'}</p>
                  <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5">
                    {i.status === 'scheduled' && i.scheduled_date ? (
                      <><Clock className="w-3 h-3" /> {L('Prévue le', 'Due on')} {new Date(i.scheduled_date).toLocaleDateString(en ? 'en-US' : 'fr-CH')}</>
                    ) : (
                      new Date(i.created_at).toLocaleDateString(en ? 'en-US' : 'fr-CH')
                    )}
                    {i.installment_total > 1 && <span> · {L('Mensualité', 'Installment')} {i.installment_number}/{i.installment_total}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-white text-sm">{(i.total || 0).toFixed(2)} CHF</span>
                  <BillingStatusPill status={i.status} />
                  <button
                    onClick={() => downloadInvoicePdf(i.invoice_number)}
                    disabled={downloadingId === i.invoice_number}
                    className="p-2 rounded-lg text-zinc-400 hover:text-primary hover:bg-white/5 transition-colors disabled:opacity-50"
                    title={L('Télécharger le PDF', 'Download PDF')}
                    data-testid={`my-invoice-download-${i.invoice_number}`}
                  >
                    {downloadingId === i.invoice_number ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
            {(billing.sessions || []).map((s) => (
              <div key={s.session_id} className="flex items-center justify-between gap-3 rounded-xl bg-zinc-800/50 border border-white/5 p-3" data-testid={`my-session-row-${s.session_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{s.description || 'Paiement Stripe'}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{new Date(s.created_at).toLocaleDateString(en ? 'en-US' : 'fr-CH')} · Stripe</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-white text-sm">{(s.amount || 0).toFixed(2)} CHF</span>
                  <BillingStatusPill status={s.payment_status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-center text-[11px] text-zinc-600 flex items-center justify-center gap-1.5 pt-1">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {L('Paiement 100% sécurisé via Stripe', '100% secure payment via Stripe')}
      </p>
    </div>
  );
};

export default SubscriptionTab;
